import { Form, Typography } from "antd";
import { SelectWithSpinAndCustomMaxTag } from "../select/SelectWithCustomMaxTag";
import { USER_DIMENSIONS } from "@src/contexts/ab-testing/ab-test-exploration-context";
import React from "react";
import { useLoadDimValue } from "@src/hooks/ab-testing/use-get-extra-value-field";
import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";
import { FormInstance } from "antd/lib";

const { Text } = Typography;
const FormName = {
    BREAKDOWN_BY: "internal-breakdown-by",
    BREAKDOWN_VALUE: "internal-breakdown-value",
};
const DimensionBreakdownComponentInternal: React.FC<{
    isReadyInitialValues: boolean;
    chartForm: FormInstance;
    onChange: (values: { breakdownBy: string; breakdownValue: string[] }) => void;
    initialValues?: {
        breakdownBy: string;
        breakdownValue: string[];
    };
}> = ({ chartForm, onChange, initialValues, isReadyInitialValues }) => {
    const isSetInitialValues = React.useRef(false);
    const { productCode, variants, abaId, amaAppIds } = useGetGeneralField({
        form: chartForm,
    });
    const [form] = Form.useForm();
    const breakdownByWatch = Form.useWatch(FormName.BREAKDOWN_BY, form);
    const breakdownValueWatch = Form.useWatch(FormName.BREAKDOWN_VALUE, form);
    const handleChange = (values: { breakdownBy: string; breakdownValue: string[] }) => {
        onChange(values);
    };

    const { isLoading, getOptions } = useLoadDimValue({
        productCode,
        amaAppIds,
        abaId,
        dimension: [breakdownByWatch],
        limit: 20,
        skip: !breakdownByWatch,
        variants,
    });
    const options = isLoading ? [] : getOptions(breakdownByWatch);

    const [isUserChangeBreakdown, setIsUserChangeBreakdown] = React.useState(false);
    React.useEffect(() => {
        if (!isUserChangeBreakdown) return;
        if (!options || options?.length === 0) {
            form.setFieldValue(FormName.BREAKDOWN_VALUE, []);
            return;
        }
        form.setFieldValue(
            FormName.BREAKDOWN_VALUE,
            options.slice(0, 5).map((opt) => opt.value),
        );
    }, [JSON.stringify(options), isUserChangeBreakdown]);

    React.useEffect(() => {
        handleChange({
            breakdownBy: breakdownByWatch,
            breakdownValue: breakdownByWatch?.length > 0 ? breakdownValueWatch : undefined,
        });
    }, [JSON.stringify([breakdownByWatch, breakdownValueWatch])]);

    React.useEffect(() => {
        if (isSetInitialValues.current || !isReadyInitialValues) return;
        form.setFieldsValue({
            [FormName.BREAKDOWN_BY]: initialValues?.breakdownBy,
            [FormName.BREAKDOWN_VALUE]: initialValues?.breakdownValue,
        });
        isSetInitialValues.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([initialValues, isSetInitialValues])]);

    return (
        <Form form={form}>
            <div className="flex  ">
                <div className="mr-4">
                    <Form.Item label="Breakdown" className="m-0" name={FormName.BREAKDOWN_BY}>
                        <SelectWithSpinAndCustomMaxTag
                            allowClear
                            showSearch
                            options={USER_DIMENSIONS?.map((item: any) => ({ label: item, value: item }))}
                            style={{ width: 250 }}
                            placeholder="Breakdown by"
                            onChange={() => {
                                setIsUserChangeBreakdown(true);
                            }}
                        />
                    </Form.Item>
                </div>

                <div
                    style={{
                        display: breakdownByWatch ? "block" : "none",
                    }}
                >
                    <Form.Item
                        className="m-0 "
                        label=""
                        required
                        name={FormName.BREAKDOWN_VALUE}
                        rules={[
                            {
                                validator() {
                                    if (!breakdownByWatch) {
                                        return Promise.resolve();
                                    }
                                    if (breakdownValueWatch?.length > 0) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject("Value to breakdown is required!");
                                },
                            },
                        ]}
                    >
                        <SelectWithSpinAndCustomMaxTag
                            placeholder="Breakdown value"
                            loading={isLoading}
                            options={options}
                            allowClear
                            filterOption={(input, option: any) => {
                                return (
                                    option?.value?.toLowerCase().includes(input.toLowerCase()) ||
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                );
                            }}
                            mode="multiple"
                            showSearch
                            optionRender={(opt) => {
                                return (
                                    <div className="grid grid-flow-row-dense grid-cols-4">
                                        <div className="col-span-3">{opt.label}</div>
                                        <div className="col-span-1">
                                            <Text disabled>{opt.data.user_count} users</Text>
                                        </div>
                                    </div>
                                );
                            }}
                            style={{
                                width: 300,
                            }}
                        />
                    </Form.Item>
                </div>
            </div>
        </Form>
    );
};

export const DimensionBreakdownComponent = React.memo(DimensionBreakdownComponentInternal, (prevProps, nextProps) => {
    return (
        JSON.stringify([prevProps.initialValues, prevProps.isReadyInitialValues]) ===
        JSON.stringify([nextProps.initialValues, nextProps.isReadyInitialValues])
    );
});
